import { useEffect, useState } from "react";
import {
  serviceStatusSchema,
  type ServiceStatus as Status,
} from "@voice/api-contract";
type StatusApi = <T>(
  path: string,
  options?: { signal?: AbortSignal },
) => Promise<T>;
type Service = Status["services"][number];
const groups = [
  {
    id: "cache-river",
    title: "Cache River services",
    ids: [
      "portal-api",
      "portal-web",
      "database",
      "redis",
      "recordings",
      "cache-river-edge",
      "cache-river-pbx",
    ],
  },
  {
    id: "shared",
    title: "Shared services",
    ids: ["reverse-proxy", "identity"],
  },
  { id: "other", title: "Other monitored systems", ids: ["mozart"] },
] as const;
const cacheRiverIds = new Set<string>(groups[0].ids);

const labels: Record<Status["services"][number]["id"], string> = {
  "portal-api": "Portal application",
  "portal-web": "Portal website",
  database: "Customer database",
  redis: "Background cache",
  recordings: "Voice recordings",
  "cache-river-edge": "Cache River SIP edge",
  "cache-river-pbx": "Cache River phone system",
  mozart: "MozartCX Enterprise",
  "reverse-proxy": "Public reverse proxy",
  identity: "Authentik · shared identity",
};
const states = {
  healthy: "Healthy",
  running: "Running",
  degraded: "Needs attention",
  unavailable: "Check failed",
  not_connected: "Not connected",
};
const failureHelp: Record<
  NonNullable<Status["services"][number]["reason"]>,
  string
> = {
  container_unavailable:
    "The collector could not read this container. Check its configured name and Docker access.",
  container_stopped:
    "The container is stopped. Review its logs before restarting it.",
  container_unhealthy:
    "The container is running, but its health check is failing. Review the application logs.",
  http_unavailable:
    "The health endpoint did not respond successfully. Check the service and its network path.",
  invalid_response:
    "The check returned an invalid response. Review the collector and service versions.",
  observer_unavailable:
    "The remote observer could not be reached. Check its SSH connection and observer service.",
  invalid_configuration:
    "This monitoring check has an invalid configuration. Review the collector settings.",
  stale_observation:
    "The observer has stopped producing current results. Check its refresh timer and logs.",
  service_degraded:
    "One or more checks failed. Review this service's running processes and health checks.",
  check_timeout:
    "The check exceeded its time limit. Check service load and network reachability.",
};
export function observationExpired(checkedAt: string, now = Date.now()) {
  const timestamp = Date.parse(checkedAt);
  return (
    !Number.isFinite(timestamp) ||
    timestamp > now + 5_000 ||
    now - timestamp > 90_000
  );
}
export async function boundedStatusRequest<T>(
  request: Promise<T>,
  timeoutMs = 10_000,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      request,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
          () => reject(new Error("Status request timed out")),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}
export async function readStatusSnapshot(
  api: StatusApi,
  signal: AbortSignal,
  timeoutMs = 10_000,
): Promise<Status> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancel = () => {};
  const interrupted = new Promise<never>((_resolve, reject) => {
    cancel = () => {
      controller.abort();
      reject(new Error("Status check canceled"));
    };
    if (signal.aborted) cancel();
    else {
      signal.addEventListener("abort", cancel, { once: true });
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error("Status request timed out"));
      }, timeoutMs);
    }
  });
  try {
    const value = await Promise.race([
      Promise.resolve().then(() => {
        if (controller.signal.aborted) throw new Error("Status check canceled");
        return api<unknown>("/admin/service-status", {
          signal: controller.signal,
        });
      }),
      interrupted,
    ]);
    return serviceStatusSchema.parse(value);
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", cancel);
    controller.abort();
  }
}
function servicePriority(row: Service, now: number) {
  if (row.stale || observationExpired(row.checkedAt, now)) return 1;
  if (["degraded", "unavailable"].includes(row.state)) return 0;
  return ["healthy", "running"].includes(row.state) ? 3 : 2;
}
export function ServiceStatusContent({
  data,
  now = Date.now(),
}: {
  data: Status;
  now?: number;
}) {
  const monitored = data.services.filter(
    (row) => row.check !== "not_configured",
  );
  const unmonitored = data.services.filter(
    (row) => row.check === "not_configured",
  );
  return (
    <>
      {data.collector === "not_connected" ? (
        <p>The service monitor is not connected yet.</p>
      ) : data.collector === "unavailable" ? (
        <p role="alert">
          The service monitor could not be read. Check the collector on Metro1.
        </p>
      ) : (
        <>
          <p className="cr-form-note">
            Last collection:{" "}
            <time dateTime={data.generatedAt!}>
              {new Date(data.generatedAt!).toLocaleString()}
            </time>
            .
            {data.collector === "stale" ||
            observationExpired(data.generatedAt!, now)
              ? " Updates have stopped. These results are out of date."
              : " Checks refresh about every 30 seconds."}
          </p>
          {groups.map((group) => {
            const rows = monitored
              .filter((row) =>
                (group.ids as readonly string[]).includes(row.id),
              )
              .sort(
                (a, b) => servicePriority(a, now) - servicePriority(b, now),
              );
            if (!rows.length) return null;
            return (
              <section
                className="cr-service-status-group"
                key={group.id}
                aria-labelledby={`service-group-${group.id}`}
              >
                <h3 id={`service-group-${group.id}`}>{group.title}</h3>
                {group.id === "shared" && (
                  <p className="cr-form-note">
                    These services are shared with other applications. Their
                    status is shown separately from Cache River's own systems.
                  </p>
                )}
                {group.id === "other" && (
                  <p className="cr-form-note">
                    This separate phone platform does not count toward Cache
                    River customer service health.
                  </p>
                )}
                <ul className="cr-service-status-list">
                  {rows.map((row) => (
                    <li key={row.id}>
                      <div>
                        <strong>{labels[row.id]}</strong>
                        <p className="cr-form-note">
                          {row.check === "not_configured"
                            ? "Monitoring connection needed"
                            : row.check === "asterisk"
                              ? "Phone-system observation"
                              : row.check === "sip_edge"
                                ? "SIP edge and media services"
                                : row.check === "http_health"
                                  ? "Application health check"
                                  : "Container health check"}
                        </p>
                        {row.reason && (
                          <p className="cr-service-diagnostic">
                            {failureHelp[row.reason]}
                          </p>
                        )}
                        <p className="cr-form-note">
                          Last checked:{" "}
                          <time dateTime={row.checkedAt}>
                            {new Date(row.checkedAt).toLocaleString()}
                          </time>
                        </p>
                        {row.registeredDevices !== undefined && (
                          <p className="cr-form-note">
                            {row.registeredDevices} registered phones
                          </p>
                        )}
                        {row.checksTotal !== undefined && (
                          <p className="cr-form-note">
                            {row.checksPassed ?? 0} of {row.checksTotal}{" "}
                            services running
                          </p>
                        )}
                        {row.activeCalls !== undefined && (
                          <p className="cr-form-note">
                            {row.activeCalls} active calls
                          </p>
                        )}
                        {row.endpoints !== undefined && (
                          <p className="cr-form-note">
                            {row.endpoints} configured endpoints
                          </p>
                        )}
                        {row.contacts !== undefined && (
                          <p className="cr-form-note">
                            {row.reachableContacts ?? "Unknown"} of{" "}
                            {row.contacts} SIP contacts reachable
                            {row.unmonitoredContacts
                              ? ` · ${row.unmonitoredContacts} not monitored`
                              : ""}
                          </p>
                        )}
                        {row.registrations !== undefined && (
                          <p className="cr-form-note">
                            {row.registered ?? "Unknown"} of {row.registrations}{" "}
                            outbound registrations registered
                          </p>
                        )}
                      </div>
                      <span
                        className={
                          row.stale ||
                          observationExpired(row.checkedAt, now) ||
                          ["degraded", "unavailable"].includes(row.state)
                            ? "cr-service-warning"
                            : ""
                        }
                      >
                        {row.stale || observationExpired(row.checkedAt, now)
                          ? "Out of date"
                          : states[row.state]}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
          {unmonitored.length > 0 && (
            <details>
              <summary>
                Monitoring not connected: {unmonitored.length} components
              </summary>
              <p className="cr-form-note">
                These components have no configured health check. This is a
                monitoring gap, not evidence of an outage or an active customer
                service.
              </p>
              <ul>
                {unmonitored.map((row) => (
                  <li key={row.id}>{labels[row.id]} — Not connected</li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
      <p className="cr-form-note">
        Phone counts cover the monitored system, not individual customer
        accounts. Some trunks do not use registration. These checks do not place
        calls or verify emergency routing.
      </p>
    </>
  );
}
export function ServiceStatusKpis({
  data,
  now = Date.now(),
}: {
  data: Status;
  now?: number;
}) {
  const current =
    data.collector === "current" &&
    !!data.generatedAt &&
    !observationExpired(data.generatedAt, now);
  const primary = data.services.filter((row) => cacheRiverIds.has(row.id));
  const healthy = primary.filter(
    (row) =>
      current &&
      !row.stale &&
      !observationExpired(row.checkedAt, now) &&
      ["healthy", "running"].includes(row.state),
  );
  const attention = primary.filter(
    (row) =>
      current &&
      !row.stale &&
      !observationExpired(row.checkedAt, now) &&
      ["degraded", "unavailable"].includes(row.state),
  );
  const unknown = primary.length - healthy.length - attention.length;
  const shared = data.services.filter((row) =>
    (groups[1].ids as readonly string[]).includes(row.id),
  );
  const sharedAttention = shared.filter(
    (row) =>
      current &&
      !row.stale &&
      !observationExpired(row.checkedAt, now) &&
      ["degraded", "unavailable"].includes(row.state),
  ).length;
  const sharedUnknown = shared.filter(
    (row) =>
      !current ||
      row.stale ||
      observationExpired(row.checkedAt, now) ||
      row.state === "not_connected",
  ).length;
  return (
    <>
      <dl className="cr-service-kpis">
        <div>
          <dt>Healthy services</dt>
          <dd>{current ? healthy.length : "—"}</dd>
        </div>
        <div>
          <dt>Needs attention</dt>
          <dd>{current ? attention.length : "—"}</dd>
        </div>
        <div>
          <dt>Unknown or outdated</dt>
          <dd>{primary.length ? unknown : "—"}</dd>
        </div>
      </dl>
      <p className="cr-form-note">
        {current
          ? "Cache River service checks only · updated about every 30 seconds."
          : "Monitoring data is unavailable or out of date."}
      </p>
      {shared.length > 0 && (
        <p
          className={
            sharedAttention || sharedUnknown
              ? "cr-service-warning"
              : "cr-form-note"
          }
        >
          Shared services: {sharedAttention} need attention · {sharedUnknown}{" "}
          unknown or outdated. View service status for details.
        </p>
      )}
    </>
  );
}
export function ServiceStatusPanel({
  api,
  summary = false,
  onViewStatus,
  refreshVersion = 0,
}: {
  api: StatusApi;
  summary?: boolean;
  onViewStatus?: () => void;
  refreshVersion?: number;
}) {
  const [snapshot, setSnapshot] = useState<{
    api: StatusApi;
    data: Status;
  } | null>(null);
  const [failure, setFailure] = useState<{ api: StatusApi } | null>(null);
  const data = snapshot?.api === api ? snapshot.data : null;
  const error = failure?.api === api;
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    async function refresh() {
      if (alive) setLoading(true);
      try {
        const result = await readStatusSnapshot(api, controller.signal);
        if (alive) {
          setSnapshot({ api, data: result });
          setFailure(null);
        }
      } catch {
        if (alive) {
          setFailure({ api });
        }
      } finally {
        if (alive) {
          setLoading(false);
          timer = setTimeout(() => void refresh(), 30_000);
        }
      }
    }
    void refresh();
    return () => {
      alive = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [api, attempt, refreshVersion]);
  return (
    <section
      className="cr-service-status"
      aria-labelledby="service-status-heading"
    >
      <div className="cr-dashboard-section-heading">
        <h2 id="service-status-heading">
          {summary ? "Service health" : "Infrastructure checks"}
        </h2>
        {summary && (
          <button className="cr-text-button" onClick={onViewStatus}>
            View service status
          </button>
        )}
        <button
          className="cr-button secondary"
          disabled={loading}
          onClick={() => setAttempt((value) => value + 1)}
        >
          {loading ? "Checking…" : "Refresh status"}
        </button>
      </div>
      {error && (
        <p role="alert">
          Service status could not refresh.{" "}
          {data
            ? "Showing the last received results with their check times. "
            : ""}
          Retrying automatically; use Refresh status to try now.
        </p>
      )}
      {data ? (
        summary ? (
          <ServiceStatusKpis data={data} now={now} />
        ) : (
          <ServiceStatusContent data={data} now={now} />
        )
      ) : (
        !error && <p role="status">Checking services…</p>
      )}
    </section>
  );
}
